import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  SafeAreaView,
  TextInput,
  Platform,
  Share,
} from 'react-native';
import LoadingAnimation from '../components/LoadingAnimation';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../services/AuthContext';
import {
  listImportBatches,
  getImportBatchById,
  getHotspotsInBatch,
  batchAction,
  deleteImportBatch,
} from '../services/database';

const ImportBatchesScreen = ({ navigation, route }) => {
  const { showToast } = useToast();
  const { appUser } = useAuth();
  const highlightBatchId = route?.params?.batchId;

  // Data state
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchHotspots, setBatchHotspots] = useState([]);
  const [loadingBatchDetail, setLoadingBatchDetail] = useState(false);

  // Modal states
  const [showBatchDetail, setShowBatchDetail] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [actionInProgress, setActionInProgress] = useState(false);

  const isAdmin = appUser?.isAdmin === true;

  useEffect(() => {
    if (!isAdmin) {
      showToast('Access denied. Admin privileges required.', 'error');
      navigation.goBack();
      return;
    }
    fetchBatches();
  }, [isAdmin]);

  // Auto-open batch detail if navigated with batchId
  useEffect(() => {
    if (highlightBatchId && batches.length > 0) {
      const batch = batches.find(b => b.id === highlightBatchId);
      if (batch) {
        handleBatchPress(batch);
      }
    }
  }, [highlightBatchId, batches]);

  const fetchBatches = useCallback(async () => {
    try {
      setLoading(true);
      const fetchedBatches = await listImportBatches(100);
      setBatches(fetchedBatches);
    } catch (error) {
      console.error('Error fetching batches:', error);
      showToast('Failed to load import batches', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleBatchPress = async (batch) => {
    try {
      setSelectedBatch(batch);
      setShowBatchDetail(true);
      setLoadingBatchDetail(true);
      
      const hotspots = await getHotspotsInBatch(batch.id);
      setBatchHotspots(hotspots);
    } catch (error) {
      console.error('Error loading batch details:', error);
      showToast('Failed to load batch details', 'error');
    } finally {
      setLoadingBatchDetail(false);
    }
  };

  const handleBatchAction = async (action) => {
    if (!selectedBatch) return;

    if (action === 'DELETE') {
      setShowDeleteModal(true);
      return;
    }

    try {
      setActionInProgress(true);
      
      if (action === 'EXPORT') {
        const result = await batchAction(selectedBatch.id, 'EXPORT');
        const exportData = JSON.stringify(result.hotspots, null, 2);
        
        if (Platform.OS === 'web') {
          const blob = new Blob([exportData], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `batch-${selectedBatch.id.substring(0, 8)}-export.json`;
          a.click();
          URL.revokeObjectURL(url);
        } else {
          await Share.share({
            message: exportData,
            title: 'Batch Export',
          });
        }
        showToast(`Exported ${result.count} hotspot(s)`, 'success');
      } else if (action === 'ARCHIVE') {
        await batchAction(selectedBatch.id, 'ARCHIVE');
        showToast('All hotspots in batch archived', 'success');
        // Refresh batch hotspots
        const hotspots = await getHotspotsInBatch(selectedBatch.id);
        setBatchHotspots(hotspots);
      } else if (action === 'PUBLISH') {
        await batchAction(selectedBatch.id, 'PUBLISH');
        showToast('All hotspots in batch published!', 'success');
        const hotspots = await getHotspotsInBatch(selectedBatch.id);
        setBatchHotspots(hotspots);
      }
    } catch (error) {
      console.error(`Error performing ${action}:`, error);
      showToast(`Failed to ${action.toLowerCase()} batch`, 'error');
    } finally {
      setActionInProgress(false);
    }
  };

  const confirmDeleteBatch = async () => {
    if (deleteConfirmText !== 'DELETE') {
      showToast('Please type DELETE to confirm', 'warning');
      return;
    }

    try {
      setActionInProgress(true);
      await deleteImportBatch(selectedBatch.id, true, 'DELETE');
      showToast('Batch and all its hotspots deleted', 'success');
      setShowDeleteModal(false);
      setShowBatchDetail(false);
      setDeleteConfirmText('');
      fetchBatches();
    } catch (error) {
      console.error('Error deleting batch:', error);
      showToast('Failed to delete batch', 'error');
    } finally {
      setActionInProgress(false);
    }
  };

  const getSourceLabel = (source) => {
    switch (source) {
      case 'AI': return '🤖 AI Generated';
      case 'ADMIN': return '👤 Admin Import';
      case 'USER': return '👥 User Submitted';
      default: return source;
    }
  };

  const getSourceColor = (source) => {
    switch (source) {
      case 'AI': return '#9C27B0';
      case 'ADMIN': return '#2196F3';
      case 'USER': return '#4CAF50';
      default: return '#666';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRegionDescription = (region) => {
    if (!region) return 'Unknown region';
    
    if (region.type === 'bounds') {
      return `Bounds: ${region.minLat?.toFixed(3)}, ${region.minLng?.toFixed(3)} → ${region.maxLat?.toFixed(3)}, ${region.maxLng?.toFixed(3)}`;
    } else if (region.type === 'radius') {
      return `Radius: ${region.radiusKm}km from ${region.centerLat?.toFixed(4)}, ${region.centerLng?.toFixed(4)}`;
    }
    return JSON.stringify(region);
  };

  const renderBatchItem = ({ item }) => {
    const isHighlighted = item.id === highlightBatchId;
    
    return (
      <TouchableOpacity
        style={[styles.batchCard, isHighlighted && styles.batchCardHighlighted]}
        onPress={() => handleBatchPress(item)}
        activeOpacity={0.7}
      >
        <View style={styles.batchHeader}>
          <View style={[styles.sourceBadge, { backgroundColor: getSourceColor(item.source) }]}>
            <Text style={styles.sourceBadgeText}>{item.source}</Text>
          </View>
          <Text style={styles.batchDate}>{formatDate(item.created_at)}</Text>
        </View>

        {item.city_name && (
          <Text style={styles.batchCity}>📍 {item.city_name}</Text>
        )}

        <View style={styles.batchStats}>
          <View style={styles.statItem}>
            <Text style={styles.statValue}>{item.total_created}</Text>
            <Text style={styles.statLabel}>Created</Text>
          </View>
          <View style={styles.statItem}>
            <Text style={styles.statValue}>{item.total_duplicates}</Text>
            <Text style={styles.statLabel}>Duplicates</Text>
          </View>
        </View>

        {item.created_by_user && (
          <Text style={styles.createdBy}>
            By: {item.created_by_user.username || 'Unknown'}
          </Text>
        )}

        <View style={styles.batchActions}>
          <Icon name="chevron-right" size={24} color="#999" />
        </View>
      </TouchableOpacity>
    );
  };

  const renderHotspotItem = ({ item }) => {
    const getStatusColor = (status) => {
      switch (status) {
        case 'PUBLISHED': return '#4CAF50';
        case 'DRAFT': return '#FF9800';
        case 'ARCHIVED': return '#9E9E9E';
        default: return '#666';
      }
    };

    return (
      <TouchableOpacity
        style={styles.hotspotItem}
        onPress={() => {
          setShowBatchDetail(false);
          navigation.navigate('AddEditHotspot', { hotspot: item });
        }}
      >
        <View style={styles.hotspotInfo}>
          <Text style={styles.hotspotName} numberOfLines={1}>{item.name}</Text>
          <Text style={styles.hotspotAddress} numberOfLines={1}>📍 {item.address}</Text>
        </View>
        <View style={[styles.statusDot, { backgroundColor: getStatusColor(item.status) }]} />
      </TouchableOpacity>
    );
  };

  if (!isAdmin) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.accessDenied}>
          <Icon name="lock" size={64} color="#ccc" />
          <Text style={styles.accessDeniedText}>Admin Access Required</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Icon name="arrow-back" size={24} color="#333" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Import Batches</Text>
        <TouchableOpacity
          style={styles.headerButton}
          onPress={() => navigation.navigate('AdminHotspotsMap')}
        >
          <Icon name="add" size={24} color="#9C27B0" />
        </TouchableOpacity>
      </View>

      {/* Info Banner */}
      <View style={styles.infoBanner}>
        <Icon name="info-outline" size={18} color="#1976D2" />
        <Text style={styles.infoBannerText}>
          Import batches track AI suggestions and bulk imports. Use batch actions to undo mistakes quickly.
        </Text>
      </View>

      {/* Content */}
      {loading ? (
        <View style={styles.loadingContainer}>
          <LoadingAnimation size={200} />
        </View>
      ) : batches.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Icon name="history" size={80} color="#ccc" />
          <Text style={styles.emptyText}>No import batches yet</Text>
          <Text style={styles.emptySubtext}>
            Use "AI Suggest" on the map to create your first batch
          </Text>
          <TouchableOpacity
            style={styles.createBatchButton}
            onPress={() => navigation.navigate('AdminHotspotsMap')}
          >
            <Icon name="auto-awesome" size={20} color="#fff" />
            <Text style={styles.createBatchButtonText}>Go to Map</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <FlatList
          data={batches}
          renderItem={renderBatchItem}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.listContent}
          showsVerticalScrollIndicator={false}
        />
      )}

      {/* Batch Detail Modal */}
      {showBatchDetail && (
        <View style={styles.fullScreenModalOverlay}>
          <SafeAreaView style={styles.detailContainer}>
            {/* Detail Header */}
            <View style={styles.detailHeader}>
              <TouchableOpacity
                style={styles.closeButton}
                onPress={() => setShowBatchDetail(false)}
              >
                <Icon name="close" size={24} color="#333" />
              </TouchableOpacity>
              <Text style={styles.detailTitle}>Batch Details</Text>
              <View style={{ width: 40 }} />
            </View>

            {selectedBatch && (
              <>
                {/* Batch Info */}
                <View style={styles.detailInfo}>
                  <View style={[styles.detailSourceBadge, { backgroundColor: getSourceColor(selectedBatch.source) }]}>
                    <Text style={styles.detailSourceText}>{getSourceLabel(selectedBatch.source)}</Text>
                  </View>
                  
                  {selectedBatch.city_name && (
                    <Text style={styles.detailCity}>📍 {selectedBatch.city_name}</Text>
                  )}
                  
                  <Text style={styles.detailDate}>
                    Created: {formatDate(selectedBatch.created_at)}
                  </Text>
                  
                  {selectedBatch.created_by_user && (
                    <Text style={styles.detailCreatedBy}>
                      By: {selectedBatch.created_by_user.username}
                    </Text>
                  )}
                  
                  <View style={styles.detailStats}>
                    <View style={styles.detailStatBox}>
                      <Text style={styles.detailStatValue}>{selectedBatch.total_created}</Text>
                      <Text style={styles.detailStatLabel}>Created</Text>
                    </View>
                    <View style={styles.detailStatBox}>
                      <Text style={styles.detailStatValue}>{selectedBatch.total_duplicates}</Text>
                      <Text style={styles.detailStatLabel}>Duplicates</Text>
                    </View>
                    <View style={styles.detailStatBox}>
                      <Text style={styles.detailStatValue}>{batchHotspots.length}</Text>
                      <Text style={styles.detailStatLabel}>Remaining</Text>
                    </View>
                  </View>

                  <Text style={styles.regionText}>
                    {getRegionDescription(selectedBatch.region)}
                  </Text>

                  {selectedBatch.notes && (
                    <Text style={styles.detailNotes}>📝 {selectedBatch.notes}</Text>
                  )}
                </View>

                {/* Batch Actions */}
                <View style={styles.batchActionBar}>
                  <TouchableOpacity
                    style={[styles.actionButton, styles.publishAllButton]}
                    onPress={() => handleBatchAction('PUBLISH')}
                    disabled={actionInProgress}
                  >
                    <Icon name="visibility" size={18} color="#fff" />
                    <Text style={styles.actionButtonText}>Publish All</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.actionButton, styles.exportButton]}
                    onPress={() => handleBatchAction('EXPORT')}
                    disabled={actionInProgress}
                  >
                    <Icon name="file-download" size={18} color="#fff" />
                    <Text style={styles.actionButtonText}>Export</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.actionButton, styles.archiveAllButton]}
                    onPress={() => handleBatchAction('ARCHIVE')}
                    disabled={actionInProgress}
                  >
                    <Icon name="archive" size={18} color="#fff" />
                    <Text style={styles.actionButtonText}>Archive All</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.actionButton, styles.deleteAllButton]}
                    onPress={() => handleBatchAction('DELETE')}
                    disabled={actionInProgress}
                  >
                    <Icon name="delete-forever" size={18} color="#fff" />
                    <Text style={styles.actionButtonText}>Delete All</Text>
                  </TouchableOpacity>
                </View>

                {/* Hotspots List */}
                <View style={styles.hotspotsSection}>
                  <Text style={styles.sectionTitle}>
                    Hotspots in this Batch ({batchHotspots.length})
                  </Text>
                  
                  {loadingBatchDetail ? (
                    <View style={styles.loadingContainer}>
                      <LoadingAnimation size={100} />
                    </View>
                  ) : batchHotspots.length === 0 ? (
                    <View style={styles.emptyHotspots}>
                      <Text style={styles.emptyHotspotsText}>
                        No hotspots in this batch
                      </Text>
                    </View>
                  ) : (
                    <FlatList
                      data={batchHotspots}
                      renderItem={renderHotspotItem}
                      keyExtractor={(item) => item.id}
                      showsVerticalScrollIndicator={false}
                    />
                  )}
                </View>
              </>
            )}
          </SafeAreaView>
        </View>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <View style={styles.modalOverlay}>
          <TouchableOpacity 
            style={styles.modalBackdrop} 
            activeOpacity={1} 
            onPress={() => {
              setShowDeleteModal(false);
              setDeleteConfirmText('');
            }}
          />
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Icon name="warning" size={48} color="#f44336" />
              <Text style={styles.modalTitle}>Delete Entire Batch</Text>
            </View>
            <Text style={styles.modalText}>
              You are about to permanently delete this batch and ALL {batchHotspots.length} hotspot(s) in it.
              This action cannot be undone.
            </Text>
            <Text style={styles.modalText}>
              Type <Text style={styles.deleteWord}>DELETE</Text> to confirm:
            </Text>
            <TextInput
              style={styles.deleteInput}
              value={deleteConfirmText}
              onChangeText={setDeleteConfirmText}
              placeholder="Type DELETE"
              autoCapitalize="characters"
            />
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmText('');
                }}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.confirmDeleteButton, deleteConfirmText !== 'DELETE' && styles.buttonDisabled]}
                onPress={confirmDeleteBatch}
                disabled={deleteConfirmText !== 'DELETE' || actionInProgress}
              >
                {actionInProgress ? (
                  <LoadingAnimation size={24} />
                ) : (
                  <Text style={styles.confirmDeleteButtonText}>Delete Forever</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
  },
  headerButton: {
    padding: 4,
  },
  infoBanner: {
    flexDirection: 'row',
    backgroundColor: '#E3F2FD',
    padding: 12,
    gap: 10,
    alignItems: 'flex-start',
  },
  infoBannerText: {
    flex: 1,
    fontSize: 13,
    color: '#1976D2',
    lineHeight: 18,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  emptyText: {
    marginTop: 16,
    fontSize: 18,
    color: '#999',
    fontWeight: '600',
  },
  emptySubtext: {
    marginTop: 8,
    fontSize: 14,
    color: '#bbb',
    textAlign: 'center',
  },
  createBatchButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 24,
    backgroundColor: '#9C27B0',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 10,
  },
  createBatchButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  listContent: {
    padding: 16,
  },
  batchCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  batchCardHighlighted: {
    borderWidth: 2,
    borderColor: '#9C27B0',
    backgroundColor: '#F3E5F5',
  },
  batchHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  sourceBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 8,
  },
  sourceBadgeText: {
    fontSize: 11,
    fontWeight: 'bold',
    color: 'white',
  },
  batchDate: {
    fontSize: 12,
    color: '#999',
  },
  batchCity: {
    fontSize: 15,
    fontWeight: '600',
    color: '#333',
    marginBottom: 12,
  },
  batchStats: {
    flexDirection: 'row',
    gap: 20,
    marginBottom: 8,
  },
  statItem: {
    alignItems: 'center',
  },
  statValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
  },
  statLabel: {
    fontSize: 12,
    color: '#666',
  },
  createdBy: {
    fontSize: 12,
    color: '#999',
  },
  batchActions: {
    position: 'absolute',
    right: 16,
    top: '50%',
    transform: [{ translateY: -12 }],
  },
  accessDenied: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
  },
  accessDeniedText: {
    fontSize: 18,
    color: '#999',
  },
  // Detail Modal Styles
  detailContainer: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  detailHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  closeButton: {
    padding: 8,
  },
  detailTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  detailInfo: {
    backgroundColor: 'white',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  detailSourceBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    marginBottom: 12,
  },
  detailSourceText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: 'white',
  },
  detailCity: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 8,
  },
  detailDate: {
    fontSize: 14,
    color: '#666',
    marginBottom: 4,
  },
  detailCreatedBy: {
    fontSize: 14,
    color: '#666',
    marginBottom: 12,
  },
  detailStats: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 12,
  },
  detailStatBox: {
    flex: 1,
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
  },
  detailStatValue: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#333',
  },
  detailStatLabel: {
    fontSize: 12,
    color: '#666',
    marginTop: 2,
  },
  regionText: {
    fontSize: 12,
    color: '#999',
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    marginBottom: 8,
  },
  detailNotes: {
    fontSize: 14,
    color: '#666',
    fontStyle: 'italic',
  },
  batchActionBar: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    padding: 16,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    gap: 6,
  },
  publishAllButton: {
    backgroundColor: '#4CAF50',
  },
  exportButton: {
    backgroundColor: '#2196F3',
  },
  archiveAllButton: {
    backgroundColor: '#9E9E9E',
  },
  deleteAllButton: {
    backgroundColor: '#f44336',
  },
  actionButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: 'white',
  },
  hotspotsSection: {
    flex: 1,
    padding: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 12,
  },
  emptyHotspots: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyHotspotsText: {
    fontSize: 14,
    color: '#999',
  },
  hotspotItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'white',
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
  },
  hotspotInfo: {
    flex: 1,
  },
  hotspotName: {
    fontSize: 15,
    fontWeight: '600',
    color: '#333',
    marginBottom: 2,
  },
  hotspotAddress: {
    fontSize: 12,
    color: '#666',
  },
  statusDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginLeft: 8,
  },
  // Full screen modal overlay (for batch details)
  fullScreenModalOverlay: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#f5f5f5',
    zIndex: 1000,
  },
  // Delete Modal Styles
  modalOverlay: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1001,
  },
  modalBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 24,
    width: '90%',
    maxWidth: 400,
    zIndex: 1002,
    ...Platform.select({
      web: {
        boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 10 },
        shadowOpacity: 0.3,
        shadowRadius: 20,
        elevation: 20,
      },
    }),
  },
  modalHeader: {
    alignItems: 'center',
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginTop: 8,
  },
  modalText: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    marginBottom: 12,
    lineHeight: 20,
  },
  deleteWord: {
    fontWeight: 'bold',
    color: '#f44336',
  },
  deleteInput: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 20,
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  cancelButton: {
    flex: 1,
    padding: 14,
    borderRadius: 8,
    backgroundColor: '#f5f5f5',
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#666',
  },
  confirmDeleteButton: {
    flex: 1,
    padding: 14,
    borderRadius: 8,
    backgroundColor: '#f44336',
    alignItems: 'center',
  },
  confirmDeleteButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: 'white',
  },
  buttonDisabled: {
    opacity: 0.5,
  },
});

export default ImportBatchesScreen;

